/**
 * SlashMenu — menú flotante para insertar bloques tipeando `/`.
 *
 * Detección: cuando el user escribe `/` al inicio de un párrafo (o
 * después de un salto), abrimos un menú con comandos disponibles. Las
 * teclas que el menú captura (↑/↓/Enter/Esc) se manejan vía
 * `editorProps.handleKeyDown` en el editor padre — este componente solo
 * pinta y expone callbacks.
 *
 * Patrón inspirado en CoAuthorix: posicionamos con `coordsAtPos`,
 * volcamos arriba si no cabe abajo, filtramos commands por la query
 * que sigue al `/`.
 */

import { forwardRef, type ComponentType } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Heading2,
  Heading3,
  List,
  ListOrdered,
  Minus,
  Quote,
  Table as TableIcon,
  Terminal,
  Type,
} from 'lucide-react'
import type { Editor } from '@tiptap/react'

export interface SlashCommand {
  /** Identificador estable para filtros y telemetría. */
  id: string
  /** i18n key dentro del namespace `editor`, segmento `slash.<id>.title`. */
  titleKey: string
  /** Igual, segmento `slash.<id>.description`. */
  descriptionKey: string
  icon: ComponentType<{ className?: string }>
  /** Aplica el comando al editor (ya con foco). */
  apply: (editor: Editor) => void
  /** Palabras clave para el filtro de búsqueda (no se traducen). */
  keywords: string[]
}

export const SLASH_COMMANDS: SlashCommand[] = [
  {
    id: 'paragraph',
    titleKey: 'slash.paragraph.title',
    descriptionKey: 'slash.paragraph.description',
    icon: Type,
    keywords: ['text', 'paragraph', 'parrafo', 'texto', 'p'],
    apply: (editor) => editor.chain().focus().setParagraph().run(),
  },
  {
    id: 'heading2',
    titleKey: 'slash.heading2.title',
    descriptionKey: 'slash.heading2.description',
    icon: Heading2,
    keywords: ['h2', 'heading', 'titulo', 'title'],
    apply: (editor) => editor.chain().focus().setHeading({ level: 2 }).run(),
  },
  {
    id: 'heading3',
    titleKey: 'slash.heading3.title',
    descriptionKey: 'slash.heading3.description',
    icon: Heading3,
    keywords: ['h3', 'heading', 'subtitulo', 'subtitle'],
    apply: (editor) => editor.chain().focus().setHeading({ level: 3 }).run(),
  },
  {
    id: 'bullet_list',
    titleKey: 'slash.bullet_list.title',
    descriptionKey: 'slash.bullet_list.description',
    icon: List,
    keywords: ['ul', 'list', 'lista', 'bullet'],
    apply: (editor) => editor.chain().focus().toggleBulletList().run(),
  },
  {
    id: 'ordered_list',
    titleKey: 'slash.ordered_list.title',
    descriptionKey: 'slash.ordered_list.description',
    icon: ListOrdered,
    keywords: ['ol', 'numbered', 'numerada', 'ordered'],
    apply: (editor) => editor.chain().focus().toggleOrderedList().run(),
  },
  {
    id: 'blockquote',
    titleKey: 'slash.blockquote.title',
    descriptionKey: 'slash.blockquote.description',
    icon: Quote,
    keywords: ['quote', 'cita', 'blockquote'],
    apply: (editor) => editor.chain().focus().toggleBlockquote().run(),
  },
  {
    id: 'horizontal_rule',
    titleKey: 'slash.horizontal_rule.title',
    descriptionKey: 'slash.horizontal_rule.description',
    icon: Minus,
    keywords: ['hr', 'divider', 'separador', 'rule'],
    apply: (editor) => editor.chain().focus().setHorizontalRule().run(),
  },
  {
    id: 'code_block',
    titleKey: 'slash.code_block.title',
    descriptionKey: 'slash.code_block.description',
    icon: Terminal,
    keywords: ['code', 'codigo', 'pre'],
    apply: (editor) => editor.chain().focus().toggleCodeBlock().run(),
  },
  {
    id: 'table',
    titleKey: 'slash.table.title',
    descriptionKey: 'slash.table.description',
    icon: TableIcon,
    keywords: ['table', 'tabla', 'grid'],
    apply: (editor) =>
      editor
        .chain()
        .focus()
        .insertTable({ rows: 3, cols: 3, withHeaderRow: true })
        .run(),
  },
]

/** Filtra comandos por la query (texto que sigue al `/`). */
export function filterSlashCommands(
  commands: SlashCommand[],
  query: string,
  // i18n para matchear también títulos traducidos.
  t: (key: string) => string,
): SlashCommand[] {
  const q = query.trim().toLowerCase()
  if (!q) return commands
  return commands.filter((cmd) => {
    if (cmd.keywords.some((k) => k.toLowerCase().includes(q))) return true
    const title = t(`editor:${cmd.titleKey}`).toLowerCase()
    if (title.includes(q)) return true
    return false
  })
}

interface SlashMenuProps {
  commands: SlashCommand[]
  selectedIndex: number
  onSelect: (cmd: SlashCommand) => void
  position: { top: number; left: number }
}

export const SlashMenu = forwardRef<HTMLDivElement, SlashMenuProps>(
  function SlashMenu(
    { commands, selectedIndex, onSelect, position }: SlashMenuProps,
    ref,
  ) {
    const { t } = useTranslation('editor')
    if (commands.length === 0) {
      return (
        <div
          ref={ref}
          style={{ position: 'fixed', top: position.top, left: position.left }}
          className="bg-surface border border-line rounded-md shadow-lg z-50 px-3 py-2 text-xs text-ink-muted"
        >
          {t('slash.no_results')}
        </div>
      )
    }
    return (
      <div
        ref={ref}
        style={{ position: 'fixed', top: position.top, left: position.left }}
        className="bg-surface border border-line rounded-md shadow-lg z-50 min-w-[260px] max-h-[280px] overflow-y-auto py-1"
      >
        {commands.map((cmd, idx) => {
          const Icon = cmd.icon
          const active = idx === selectedIndex
          return (
            <button
              key={cmd.id}
              type="button"
              onMouseDown={(e) => {
                // mousedown (no click) para que TipTap no pierda la
                // selección antes de aplicar el comando.
                e.preventDefault()
                onSelect(cmd)
              }}
              className={
                active
                  ? 'w-full text-left px-3 py-2 flex items-center gap-2.5 bg-brand-soft text-ink'
                  : 'w-full text-left px-3 py-2 flex items-center gap-2.5 text-ink hover:bg-surface-muted'
              }
            >
              <Icon className="w-4 h-4 shrink-0 text-ink-muted" />
              <div className="min-w-0">
                <div className="text-xs font-medium truncate">
                  {t(cmd.titleKey as 'slash.paragraph.title')}
                </div>
                <div className="text-[10px] text-ink-subtle truncate">
                  {t(cmd.descriptionKey as 'slash.paragraph.description')}
                </div>
              </div>
            </button>
          )
        })}
      </div>
    )
  },
)
